import { Fragment, useCallback } from 'react'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'

import { Alarm, Appliance, ApplianceType, Group, PhysicalPort, SharedPort, Network } from 'common/api/v1/types'
import { Autocomplete, GridItem, Paper } from '../../../common/Form'
import { Api } from '../../../../store'
import { getInterfaceName, isEditableInterface, useUser } from '../../../../utils'

export interface ISharedPort extends SharedPort {
  name: PhysicalPort['name']
  _owner: Group
  _alarmsThatDisablePort?: Alarm[]
  _address?: string
  _publicAddress?: string
  _interRegionPublicAddress?: string
  _networks?: Network[]
}

interface IApplianceFormInterfacesProps {
  ports: ISharedPort[]
  appliance: Appliance
}

const PortAlarms = ({ alarms }: { alarms: Alarm[] | undefined }) => {
  if (!alarms) {
    return <></>
  }

  return (
    <>
      {alarms.map((alarm) => (
        <GridItem key={alarm.alarmId} xs={4} lg={4} xl={4}>
          <Typography component="div" variant="body2" color="textSecondary">
            {alarm.text}
          </Typography>
        </GridItem>
      ))}
    </>
  )
}

const Interfaces = ({ ports, appliance }: IApplianceFormInterfacesProps) => {
  const isCoreNode = appliance.type === ApplianceType.core
  const isThumbNode = appliance.type === ApplianceType.thumb

  const user = useUser()
  const applianceOwnerId = typeof appliance.owner === 'string' ? appliance.owner : appliance.owner?.id
  const isUserAllowedToEdit = applianceOwnerId && isEditableInterface(applianceOwnerId, user)

  const canEditInterfaceOwner = (_port: ISharedPort) => isUserAllowedToEdit && !isCoreNode && !isThumbNode

  const groupsApi = useCallback(Api.groupsApi.getPureGroups.bind(Api.groupsApi), [])
  const networksApi = useCallback(Api.networksApi.getNetworks.bind(Api.networksApi), [])
  return (
    <Paper title="Interfaces" collapsible>
      <List style={{ width: '100%' }}>
        {ports.map((port, ind) => (
          <Fragment key={port.port}>
            <ListItem sx={{ padding: 0 }}>
              <Paper>
                <GridItem lg={12} xl={12}>
                  <ListItemText
                    disableTypography
                    primary={<Typography>{getInterfaceName({ ...port, appliance })}</Typography>}
                    secondary={
                      <>
                        <Typography component="div" variant="body2" color="textSecondary">
                          {port._address}
                        </Typography>
                        {!!port._publicAddress && (
                          <Typography component="div" variant="body2" color="textSecondary">
                            Public address: {port._publicAddress}
                          </Typography>
                        )}
                        {!!port._interRegionPublicAddress && (
                          <Typography component="div" variant="body2" color="textSecondary">
                            Inter region address: {port._interRegionPublicAddress}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <PortAlarms alarms={port._alarmsThatDisablePort} />
                </GridItem>

                <Autocomplete<Group>
                  name={`ports.${ind}.owner`}
                  label="Owner group"
                  api={groupsApi}
                  defaultOption={port.owner ? { id: port.owner, name: port._owner.name } : undefined}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  optionComparator={(o1, o2) => o1.id == o2.id}
                  required={true}
                  disabled={!canEditInterfaceOwner(port)}
                  xs={4}
                />

                <Autocomplete<Network>
                  name={`ports.${ind}._networks`}
                  label="Networks"
                  api={networksApi}
                  defaultOption={port._networks}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  optionComparator={(o1, o2) => o1.id == o2.id}
                  multiple
                  xs={4}
                  tooltip="Interfaces within the same network will be connected using their private IP addresses"
                  disabled={!isUserAllowedToEdit}
                />
              </Paper>
            </ListItem>
            <Divider />
          </Fragment>
        ))}
      </List>
    </Paper>
  )
}

export default Interfaces
